<template>
  <div class="client-general">
    <div class="client-general-information">
      <p class="client-general-information_title">STB Client ID</p>
      <p class="client-general-information_content">{{ clientSidLabel }}</p>
    </div>
    <div class="client-general-information">
      <p class="client-general-information_title">CS Client ID</p>
      <p id="client-general-cs-client-id" class="client-general-information_content">
        {{ csClientId }}
      </p>
    </div>

    <v-list v-if="clientGeneralData.subdomain != ''">
      <v-list-item class="client-site-link-item">
        <a
          id="client-general-site-link"
          class="client-site-link"
          :href="getClientSiteUrl(clientGeneralData.subdomain)"
          target="_blank"
        >
          Go to the client site &nbsp;<v-icon
            class="client-site-link-icon"
            color="primary"
            size="x-small"
            >mdi-open-in-new</v-icon
          >
        </a>
      </v-list-item>
    </v-list>
    <v-spacer></v-spacer>
    <v-row v-if="clientSid != ''">
      <v-col>
        <div>Client Logo</div>
        <div class="crop-note">You can click the button below to select another Logo to crop</div>
      </v-col>
    </v-row>

    <image-upload
      id="client-general-image-upload"
      :image-url="logo ? logo.logoAttachmentUrl : ''"
      :reset-view="isClientSaved"
      :disable-crop-button="cropButtonDisabled || saveActionInProgress"
      @image-changed="onImageChanged"
    ></image-upload>
    <v-spacer></v-spacer>
    <v-row>
      <v-col cols="12">
        <div>Client Logo Background Colour</div>
        <v-text-field
          id="txtClientLogoBackgroundColour"
          v-model="logo.clientLogoBackgroundColor"
          variant="solo"
          class="client-input"
          placeholder="Leave blank for default or enter in a hexadecimal color value"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>Client Name <span class="text-red">*</span></div>
        <v-text-field
          id="txtClientName"
          v-model="clientGeneralData.clientName"
          variant="solo"
          class="client-input"
          placeholder="Client Name"
          required
          :error-messages="clientNameErrors"
          density="compact"
          @update:model-value="v$.clientGeneralData.clientName.$touch()"
          @blur="v$.clientGeneralData.clientName.$touch()"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>Client Subdomain <span class="text-red">*</span></div>
        <v-text-field
          id="txtSubdomain"
          v-model="clientGeneralData.subdomain"
          variant="solo"
          class="client-input"
          placeholder="Client Subdomain"
          required
          :error-messages="subdomainErrors"
          density="compact"
          @update:model-value="onSubdomainChange"
          @blur="onSubdomainChange"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>IdP URL</div>
        <v-text-field
          id="txtIdpUrl"
          v-model="clientGeneralData.idpUrl"
          variant="solo"
          class="client-input"
          placeholder="IdP URL"
          :error-messages="idpUrlErrors"
          density="compact"
          @update:model-value="v$.clientGeneralData.idpUrl.$touch()"
          @blur="v$.clientGeneralData.idpUrl.$touch()"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>Time Zone <span class="text-red">*</span></div>
        <timezone-selector
          id="client-general-time-zone-selector"
          v-model="clientGeneralData.timezone"
          :error-messages="timezoneErrors"
          :hide-selected="hideSelected"
          @update:model-value="v$.clientGeneralData.timezone.$touch()"
        >
        </timezone-selector>
        <div
          v-if="0 < timezoneErrors.length"
          id="client-general-timezone-error"
          class="pre-clients-error"
        >
          Time Zone is required
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="client-individual-client-options">
        <div>Client Options</div>
        <v-card elevation="2" border>
          <v-card-text class="client-options-content">
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxEnableAdditionalInformation"
                  v-model="clientOptions.excursionAdditionalInformationEnabled"
                  color="primary"
                  :disabled="disableAdditional"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-additional-information-label"
                class="client-general-checkbox-label"
                @click="enableAdditionalInformation()"
              >
                Enable Additional Information
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxEnableHighRiskStudentMedical"
                  v-model="clientOptions.excursionHighRiskStudentMedicalEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-high-risk-student-medical-label"
                class="client-general-checkbox-label"
                @click="
                  clientOptions.excursionHighRiskStudentMedicalEnabled =
                    !clientOptions.excursionHighRiskStudentMedicalEnabled
                "
              >
                Enable High-Risk Student Medical
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxEnablePersonResponsible"
                  v-model="clientOptions.excursionPersonResponsibleEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-person-responsible-label"
                class="client-general-checkbox-label"
                @click="
                  clientOptions.excursionPersonResponsibleEnabled =
                    !clientOptions.excursionPersonResponsibleEnabled
                "
              >
                Enable Person Responsible
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxEnablePolicyView"
                  v-model="clientOptions.excursionPolicyViewActionEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-policy-view-label"
                class="client-general-checkbox-label"
                @click="
                  clientOptions.excursionPolicyViewActionEnabled =
                    !clientOptions.excursionPolicyViewActionEnabled
                "
              >
                Enable Policy View
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxEnableChecklistEnforcement"
                  v-model="clientOptions.excursionChecklistEnforcementBeforeSubmissionEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-checklist-enforcement-before-submission-label"
                class="client-general-checkbox-label"
                @click="
                  clientOptions.excursionChecklistEnforcementBeforeSubmissionEnabled =
                    !clientOptions.excursionChecklistEnforcementBeforeSubmissionEnabled
                "
              >
                Enable Checklist Enforcement Before Submission
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxExcursionCopyActionEnabled"
                  v-model="clientOptions.excursionCopyActionEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-copy-excursion-label"
                class="client-general-checkbox-label"
                @click="
                  clientOptions.excursionCopyActionEnabled =
                    !clientOptions.excursionCopyActionEnabled
                "
              >
                Enable Copy Excursion
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxIdpRbaclEnabled"
                  v-model="clientOptions.idpRbaclEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-idp-rbacl-label"
                class="client-general-checkbox-label"
                @click="clientOptions.idpRbaclEnabled = !clientOptions.idpRbaclEnabled"
              >
                Enable IdP RBACL
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxAuth0LoginEnabled"
                  v-model="clientOptions.auth0LoginEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-auth0-login-label"
                class="client-general-checkbox-label"
                @click="clientOptions.auth0LoginEnabled = !clientOptions.auth0LoginEnabled"
              >
                Enable Auth0 Login
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxPublicSchoolFeaturesetEnabled"
                  v-model="clientOptions.publicSchoolFeatureSetEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-public-school-feature-set-label"
                class="client-general-checkbox-label"
                for="client-general-public-school-feature-set-enabled"
                @click="
                  clientOptions.publicSchoolFeatureSetEnabled =
                    !clientOptions.publicSchoolFeatureSetEnabled
                "
              >
                Enable Public School Feature Set
              </v-col>
            </v-row>
            <v-row align="center" class="client-general-checkbox-layout">
              <v-col cols="auto" class="client-general-checkbox">
                <v-checkbox-btn
                  id="cbxExcursionInherentRiskRatingEnabled"
                  v-model="clientOptions.excursionInherentRiskRatingEnabled"
                  class="client-general-checkbox"
                  density="compact"
                  hide-details
                  color="primary"
                ></v-checkbox-btn>
              </v-col>
              <v-col
                id="client-general-enable-excursion-inherent-risk-rating-label"
                class="client-general-checkbox-label"
                for="client-general-excursion-inherent-risk-rating-enabled"
                @click="
                  clientOptions.excursionInherentRiskRatingEnabled =
                    !clientOptions.excursionInherentRiskRatingEnabled
                "
              >
                Enable Excursion Inherent Risk Rating
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-spacer></v-spacer>
    <v-row>
      <v-col cols="12" class="d-flex justify-end align-center">
        <cs-button
          v-if="editMode === 'update'"
          id="btnSaveGeneral"
          primary
          label="Save General"
          :disabled="saveButtonDisabled"
          :loading="saveActionInProgress"
          @click="onSave()"
        ></cs-button>
      </v-col>
    </v-row>
    <cs-form-dialog
      id="dlg-client-ind-editor-generic"
      v-model="noticeDialogDisplayed"
      body-id="dlg-client-ind-editor-generic-body"
      heading="Client General Editor"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="onDialogOkBtn"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="noticeDialogMessage"></div>
        <v-spacer></v-spacer>
        <div v-safe-html="noticeDialogWarningMessage" class="warning-message"></div>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { mapGetters } from 'vuex';
import * as types from '@/store/mutationTypes';
import { validateUrl, validateSubdomain } from './validation';
import ImageUpload from './ImageUpload';
import attachmentCategory from '../lib/const/attachmentCategory';
import TimezoneSelector from './TimezoneSelector';

export default {
  name: 'ClientGeneral',

  components: {
    ImageUpload,
    TimezoneSelector
  },

  extends: CSBase,

  props: {
    clientSid: { type: String, required: false, default: '' },
    // @TODO to allow SaveAll to show domain conflict. To be removed after consolidation
    showDomainConflict: { type: Boolean, required: false, default: false }
  },

  emits: [
    'clientGeneralChanged',
    'clientLogoChanged',
    'clientOptionsChanged',
    'subdomainChanged',
    'clientGeneralSaved'
  ],

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      editMode: { type: String, required: false, default: 'create' },
      csClientId: '-',
      subdomainConflicted: false,
      clientGeneralData: {
        clientId: '',
        clientName: '',
        subdomain: '',
        idpUrl: '',
        timezone: null
      },
      clientOptions: {
        excursionAdditionalInformationEnabled: false,
        excursionChecklistEnforcementBeforeSubmissionEnabled: false,
        excursionCopyActionEnabled: false,
        excursionHighRiskStudentMedicalEnabled: false,
        excursionPersonResponsibleEnabled: false,
        excursionPolicyViewActionEnabled: false,
        excursionInherentRiskRatingEnabled: false,
        idpRbaclEnabled: false,
        auth0LoginEnabled: false,
        publicSchoolFeatureSetEnabled: false
      },
      logo: {
        imgSrc: '',
        logoFileChanged: false,
        clientLogoBackgroundColor: '',
        logoAttachmentUrl: '',
        logoFile: undefined
      },
      cropButtonDisabled: false,
      saveActionInProgress: false,
      noticeDialogDisplayed: false,
      noticeDialogMessage: '',
      noticeDialogWarningMessage: '',
      isErrorNotice: false,
      isClientSaved: false,
      // @TODO to check how this is used
      hideSelected: false
    };
  },

  computed: {
    clientSidLabel() {
      return this.clientSid > 0 ? this.clientSid : '-';
    },
    ...mapGetters({
      clientGeneralView: 'clients/clientGeneralView'
    }),
    clientNameErrors() {
      const errors = [];
      if (!this.v$.clientGeneralData.clientName.$dirty) return errors;
      if (this.v$.clientGeneralData.clientName.required.$invalid) {
        errors.push('Client Name is required');
      }
      return errors;
    },
    timezoneErrors() {
      const errors = [];
      if (!this.v$.clientGeneralData.timezone.$dirty) return errors;
      if (this.v$.clientGeneralData.timezone.required.$invalid) {
        errors.push('Time Zone is required');
      }
      return errors;
    },
    subdomainErrors() {
      const errors = [];
      if (!this.v$.clientGeneralData.subdomain.$dirty) return errors;
      if (this.v$.clientGeneralData.subdomain.required.$invalid) {
        errors.push('Client Subdomain is required');
      } else if (this.v$.clientGeneralData.subdomain.validateSubdomain.$invalid) {
        errors.push('Invalid Client Subdomain');
      } else if (this.subdomainConflicted) {
        errors.push('Client Subdomain conflicted');
      }
      return errors;
    },
    idpUrlErrors() {
      const errors = [];
      if (!this.v$.clientGeneralData.idpUrl.$dirty) return errors;
      if (this.v$.clientGeneralData.idpUrl.validateIdpUrl.$invalid) {
        errors.push('Invalid IdP URL');
      }
      return errors;
    },
    saveButtonDisabled() {
      return this.v$.$invalid || this.saveActionInProgress;
    },
    disableAdditional() {
      return this.editMode === 'update' && this.clientOptions.excursionAdditionalInformationEnabled;
    }
  },

  watch: {
    async clientSid() {
      this.initPage(this.clientSid);
    },
    clientGeneralView() {
      this.reloadData();
    },

    clientGeneralData: {
      handler() {
        this.$emit('clientGeneralChanged', this.clientGeneralData);
      },
      deep: true
    },

    logo: {
      handler() {
        this.$emit('clientLogoChanged', this.logo);
      },
      deep: true
    },

    clientOptions: {
      handler() {
        this.$emit('clientOptionsChanged', this.clientOptions);
      },
      deep: true
    },
    // @TODO to allow SaveAll to show domain conflict. To be removed after consolidation
    showDomainConflict() {
      this.subdomainConflicted = this.showDomainConflict;
    }
  },

  validations: {
    clientGeneralData: {
      clientName: {
        required
      },
      timezone: {
        required
      },
      subdomain: {
        required,
        validateSubdomain
      },
      idpUrl: {
        validateIdpUrl: validateUrl
      }
    }
  },

  created() {},

  async mounted() {
    this.initPage(this.clientSid);
  },

  methods: {
    async initPage(clientSid) {
      if (clientSid !== '') {
        this.editMode = 'update';
        await this.fetchClientGeneral();
        this.reloadData();
      }
    },

    getClientSiteUrl(subdomain) {
      const { protocol, host } = window.location;

      return `${protocol}//${subdomain}.${host}`;
    },

    async fetchClientGeneral() {
      try {
        if (this.clientSid) {
          this.setLoading(true);
          await this.$store.dispatch('clients/fetchClientGeneral', this.clientSid);
          this.clearLoading();
        }
      } catch (e) {
        this.clearLoading();
      }
    },

    onImageChanged(logoFile) {
      this.logo.logoFileChanged = true;
      this.logo.logoFile = logoFile;
    },

    async saveLogo(clientSid) {
      if (!this.logo.logoFileChanged) {
        return {};
      }
      // post image
      const requestFormData = new FormData();
      requestFormData.append('category', attachmentCategory.LOGO);

      requestFormData.append('file', this.logo.logoFile);

      const argHash = {
        sid: this.clientSid || clientSid,
        payload: requestFormData,
        store: this.$store
      };
      const cbResponse = await this.$app.stbApiAdapter.postClientAttachmentCategories(argHash);
      return cbResponse?.data;
    },

    enableAdditionalInformation() {
      if (!this.disableAdditional) {
        this.clientOptions.excursionAdditionalInformationEnabled =
          !this.clientOptions.excursionAdditionalInformationEnabled;
      }
    },

    async saveClientGeneral() {
      const clientGeneral = { ...this.clientGeneralData };
      clientGeneral.clientSid = this.clientSid;
      clientGeneral.clientOptions = { ...this.clientOptions };
      clientGeneral.clientLogo = {
        clientLogoBackgroundColor: this.logo.clientLogoBackgroundColor,
        logoAttachmentUrl: this.logo.logoAttachmentUrl
      };
      const patchRes = await this.$store.dispatch('clients/patchClientGeneral', clientGeneral);

      return patchRes;
    },

    async onSave() {
      this.saveActionInProgress = true;
      this.subdomainConflicted = false;
      try {
        this.$store.commit('app/setSilentAxiosError', true);
        const saveGeneralRes = await this.saveClientGeneral();
        if (!saveGeneralRes || saveGeneralRes.status !== 'success') {
          this.saveActionInProgress = false;
          this.handleSaveError(saveGeneralRes.error);
          return;
        }
        if (this.logo.logoFileChanged) {
          const saveLogoRes = await this.saveLogo();
          if (!saveLogoRes || saveLogoRes.error) {
            this.alertSaveResult('error', 'General Setting saved, but logo failed to be saved.');
            this.reloadData();
            this.saveActionInProgress = false;
            return;
          }
        }
        this.alertSaveResult('success');
        this.fetchClientGeneral();
        this.$emit('clientGeneralSaved', true);
      } catch (e) {
        this.handleSaveError(e);
      } finally {
        this.cropButtonDisabled = false;
        this.saveActionInProgress = false;
      }
    },

    handleSaveError(error) {
      let displayMessage = error.message || '';
      let warningMessage = '';
      try {
        const errorMessages = error.message?.split(':') || [];
        if (errorMessages.length > 3) {
          // eslint-disable-next-line prefer-destructuring
          displayMessage = `Subdomain Conflict: ${errorMessages[2]}`;
          warningMessage = `errorRefId: ${errorMessages[1]}`;
        }
        if (error.statusCode === 409) {
          this.subdomainConflicted = true;
        }
        this.alertSaveResult(
          'error',
          `Failed to save Client information. <br/> ${displayMessage}`,
          warningMessage
        );
      } finally {
        this.$store.commit('app/setSilentAxiosError', true);
      }
    },

    alertSaveResult(status, errorMessage, warningMessage) {
      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.isErrorNotice = false;
      this.noticeDialogWarningMessage = '';
      if (status === 'success') {
        // success
        this.noticeDialogMessage = 'General Setting has been saved.';
        this.noticeDialogWarningMessage = warningMessage || '';
        this.isClientSaved = true;
      } else {
        // failed
        this.isClientSaved = false;
        this.isErrorNotice = true;
        this.noticeDialogMessage = errorMessage || `Failed to save Client information.`;
        this.noticeDialogWarningMessage = warningMessage || '';
      }
      this.noticeDialogDisplayed = true;
    },

    onDialogOkBtn() {
      this.noticeDialogDisplayed = false;
      this.isErrorNotice = false;
    },

    reloadData() {
      this.clear();
      const { clientName, subdomain, idpUrl, timezone } = this.clientGeneralView;
      this.clientGeneralData = { clientName, subdomain, idpUrl, timezone };
      this.logo = { ...this.logo, ...this.clientGeneralView.clientLogo };
      this.clientOptions = { ...this.clientOptions, ...this.clientGeneralView.clientOptions };
    },

    clear() {
      this.cropButtonDisabled = false;
    },

    onSubdomainChange() {
      this.v$.clientGeneralData.subdomain.$touch();
      this.subdomainConflicted = false;
      this.$emit('subdomainChanged', this.clientGeneralData.subdomain);
    }
  }
};
</script>
<style scoped>
.client-general-information {
  display: flex;
}

.client-general-information p {
  margin-bottom: 0;
}

.client-general-information_title {
  width: 15%;
}

.client-general-information_content {
  width: 85%;
}

.warning-message {
  font-size: 10pt;
  padding-top: 12px;
  color: var(--v-error-base);
  font-style: italic;
}

.client-general-checkbox {
  margin-top: 0 !important;
  font-size: 16px;
}

.client-input {
  margin-bottom: 12px;
}

.client-general-checkbox-layout {
  height: 50px;
  padding-left: 5px;
}

.client-options-content {
  padding-bottom: 30px;
}

.client-site-link {
  text-decoration: none;
  color: rgb(var(--v-theme-primary));
}
</style>
